import {
  ArrowRight01Icon,
  FlashIcon,
  FlowIcon,
  GraduationScrollIcon,
  Link01Icon,
  Notification01Icon,
  StarAward01Icon,
  User02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/hooks/usePermissions';

interface AdminMenuItem {
  titleKey: string;
  descriptionKey: string;
  url: string;
  icon: any;
  requiredPermissions: string[];
}

const adminMenuItems: AdminMenuItem[] = [
  {
    titleKey: 'sidebar.management.userManagement',
    descriptionKey: 'admin.userManagementDesc',
    url: '/user-management',
    icon: User02Icon,
    requiredPermissions: ['user.read', 'role.read', 'permission.read'],
  },
  {
    titleKey: 'sidebar.management.trainingManagement',
    descriptionKey: 'admin.trainingManagementDesc',
    url: '/training-management',
    icon: GraduationScrollIcon,
    requiredPermissions: ['training.create'],
  },
  {
    titleKey: 'sidebar.management.gamificationManagement',
    descriptionKey: 'admin.gamificationManagementDesc',
    url: '/admin/gamification',
    icon: StarAward01Icon,
    requiredPermissions: ['gamification.manage'],
  },
  {
    titleKey: 'sidebar.management.notifications',
    descriptionKey: 'admin.notificationsDesc',
    url: '/notifications',
    icon: Notification01Icon,
    requiredPermissions: ['notification.read', 'user.read'],
  },
  {
    titleKey: 'sidebar.management.workflows',
    descriptionKey: 'admin.workflowsDesc',
    url: '/admin/workflows',
    icon: FlowIcon,
    requiredPermissions: ['workflow.read'],
  },
  {
    titleKey: 'sidebar.nav.integration',
    descriptionKey: 'admin.integrationsDesc',
    url: '/integrations',
    icon: Link01Icon,
    requiredPermissions: ['webhook.read'],
  },
  {
    titleKey: 'sidebar.management.dap',
    descriptionKey: 'admin.dapDesc',
    url: '/admin/dap/walkthroughs',
    icon: FlashIcon,
    requiredPermissions: ['dap.read'],
  },
];

function AdminIndexPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAllPermissions } = usePermissions();

  const visibleItems = adminMenuItems.filter((item) => hasAllPermissions(item.requiredPermissions));

  if (visibleItems.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('sidebar.groups.admin')}</h1>
          <p className="text-muted-foreground">{t('admin.noAccessDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('sidebar.groups.admin')}</h1>
        <p className="text-muted-foreground">{t('admin.description')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleItems.map((item) => (
          <Card
            key={item.url}
            className="cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate({ to: item.url })}
          >
            <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <HugeiconsIcon icon={item.icon} className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base">{t(item.titleKey)}</CardTitle>
              </div>
              <HugeiconsIcon
                icon={ArrowRight01Icon}
                className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              <p className="text-sm text-muted-foreground line-clamp-2">{t(item.descriptionKey)}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminIndexPage,
});
